import { FieldState, FormState } from "formstate";
import { inject, observer } from "mobx-react";
import React from "react";
import { Link, Redirect } from "react-router-dom";
import { AgentInput } from "@fidget/common/components/form/agentInput";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { parseQueryString } from "@fidget/common/utils";
import {
  PasswordValidation,
  validatePasswordPolicy
} from "@fidget/common/utils/validatePasswordPolicy";
import { validatePassword, validateRequired } from "@fidget/common/utils/validatorsFormState";
import { InjectedCurrentUserStore, ResetPassStatus } from "../../../models/currentUser";
import { ICurrentUserStore } from "../../../models/currentUser/CurrentUserStore";
import { Path } from "../../../models/router";
import { PasswordRules } from "../../passwordRules";
import { BeforeLoginSection } from "../components/beforeLoginSection";
import { WelcomeToHeader } from "../components/welcomeToHeader";
import styles from "./login.css";

type PasswordFormFields = {
  password: FieldState<string>;
  passwordRepeated: FieldState<string>;
};

interface Props {
  currentUserStore?: ICurrentUserStore;
}

interface State {
  passwordValidation: PasswordValidation;
}

@inject("currentUserStore")
@observer
export class ResetPassword extends React.Component<Props, State> {
  token?: string;
  email?: string;
  styleType: "login" = "login";
  passwordFields: PasswordFormFields;
  passwordForm: FormState<PasswordFormFields>;

  constructor(props: Props) {
    super(props);
    this.state = {
      passwordValidation: {
        longEnough: false,
        hasOneNumber: false,
        hasUppercase: false
      }
    };

    this.passwordFields = {
      password: new FieldState("")
        .disableAutoValidation()
        .validators(validateRequired, validatePassword),
      passwordRepeated: new FieldState("")
        .disableAutoValidation()
        .validators(this.validatePasswordRepeated)
    };
    this.passwordForm = new FormState(this.passwordFields).validators(($) =>
      $.password.$ !== $.passwordRepeated.$
        ? intl.formatMessage("passwordValidation.passMismatch")
        : ""
    );
  }

  get injected(): InjectedCurrentUserStore {
    return this.props as InjectedCurrentUserStore;
  }

  componentDidMount() {
    this.injected.currentUserStore.clearErrors();
    const { token, email } = parseQueryString();
    this.token = token;
    this.email = email;
  }

  validatePasswordRepeated = (val: string) => {
    const { password } = this.passwordFields;
    return (
      !password.hasError &&
      val !== password.value &&
      intl.formatMessage("passwordValidation.passMismatch")
    );
  };

  handlePasswordChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ passwordValidation: validatePasswordPolicy(ev.target.value) });
  };

  isButtonDisabled = () =>
    Object.values(this.state.passwordValidation).includes(false) ||
    !this.passwordFields.passwordRepeated.value;

  handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const result = await this.passwordForm.validate();

    if (!result.hasError) {
      this.injected.currentUserStore.changePasswordWithToken(
        this.token || "",
        result.value.password.value
      );
    }
  };

  render() {
    const { password, passwordRepeated } = this.passwordFields;
    return this.injected.currentUserStore.resetPassStatus === ResetPassStatus.FINISHED ? (
      <Redirect to={Path.LOGIN} />
    ) : (
      <div className={styles["login-background"]}>
        <WelcomeToHeader styleType={this.styleType}>
          <BeforeLoginSection styleType={this.styleType}>
            <header>
              <h2>
                <L10n id={"login.resetPassword.changeTitle"} values={{ email: this.email }} />
              </h2>
              <PasswordRules
                passwordValidation={this.state.passwordValidation}
                okColor={"#7AE1BF"}
              />
            </header>
            <form key={"passChangeForm"} onSubmit={this.handleSubmit}>
              <AgentInput
                key={"newPassword"}
                field={password}
                styleType={this.styleType}
                type={"password"}
                onChange={this.handlePasswordChange}
                placeholder={intl.formatMessage("login.password.placeholder.newPassword")}
                autoComplete={"new-password"}
                autoFocus
              />
              <AgentInput
                key={"newPasswordRepeated"}
                field={passwordRepeated}
                styleType={this.styleType}
                type={"password"}
                placeholder={intl.formatMessage("login.password.placeholder.repeatNewPassword")}
                autoComplete={"new-password"}
              />
              <div>
                <Link to={Path.LOGIN} className={styles.button}>
                  <L10n id={"login.resetPassword.cancel"} />
                </Link>
                <button type={"submit"} disabled={this.isButtonDisabled()}>
                  <L10n id={"login.password.button.change"} />
                </button>
              </div>
            </form>
            <div className={styles.footer}>
              <p>
                <L10n id={"login.notYourEmail"} />
              </p>
              <p>
                <Link to={Path.LOGIN}>
                  <L10n id={"login.backToSignIn"} />
                </Link>
              </p>
            </div>
          </BeforeLoginSection>
        </WelcomeToHeader>
      </div>
    );
  }
}
